import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  getJobById,
  getWorkAssignmentByCurrentRecruiterAndJob,
  IJob,
  IWorkAssignment,
} from 'wisestep-api';

import JobPageParams from '../../../features/jobs/types/JobPageParams';

interface JobCRMContextType {
  job: IJob | null;
  workAssignment: IWorkAssignment | null;
  getWorkAssignment: () => void;
  loadingJobInfo: boolean;
  getJobDetails: () => void;
}

const JobCRMContext = createContext<JobCRMContextType | undefined>(undefined);

export const JobCRMProvider = ({ children }: { children: ReactNode }) => {
  const params = useParams<JobPageParams>();
  const jobId = params.jobId ?? '';
  const [job, setJob] = useState<IJob | null>(null);
  const [workAssignment, setWorkAssignment] = useState<IWorkAssignment | null>(null);
  const [loadingJobInfo, setLoadingJobInfo] = useState<boolean>(false);

  const getJobDetails = useCallback(async () => {
    if (!jobId) return;
    try {
      const res = await getJobById(jobId);
      setJob(res?.data ?? null);
    } catch (e) {
      console.error('Failed to load the Job Data', e);
      setJob(null);
    }
  }, [jobId]);

  const getWorkAssignment = useCallback(async () => {
    if (!jobId) return;
    try {
      const res = await getWorkAssignmentByCurrentRecruiterAndJob(jobId);
      setWorkAssignment(res?.data ?? null);
    } catch (e) {
      console.error('Failed to load the workAssignment', e);
      setWorkAssignment(null);
    }
  }, [jobId]);

  useEffect(() => {
    const fetchAll = async () => {
      setLoadingJobInfo(true);
      await Promise.all([getJobDetails(), getWorkAssignment()]);
      setLoadingJobInfo(false);
    };
    fetchAll();
  }, [getJobDetails, getWorkAssignment]);

  return (
    <JobCRMContext.Provider
      value={{ job, workAssignment, getWorkAssignment, loadingJobInfo, getJobDetails }}
    >
      {children}
    </JobCRMContext.Provider>
  );
};

export const useJobCRMContext = (): JobCRMContextType => {
  const context = useContext(JobCRMContext);
  if (!context) {
    throw new Error('useJobCRM must be used within a JobCRMProvider');
  }
  return context;
};
